import { request } from './request'
import { useServerFetch } from '../composables/useServerFetch'
import { useEnvApi } from '../composables/useEnvApi'

const REGULATOR_DISCLOSURE_DETAIL_API_PATH = '/api/v4/bit/web/regulator/disclosures/detail'
const REGULATOR_DISCLOSURE_LIST_API_PATH = '/api/v4/bit/web/regulator/disclosures/list'

export type RegulatorDisclosureTag = {
  tagName?: string
  color?: string
  category?: string
  language?: string
  logo?: string
}

export type RegulatorDisclosureRegInfo = {
  fullName?: string
  enFullname?: string
  shortName?: string
  logo?: string
  themeColor?: string
  time?: string
  websites?: string[]
  labels?: string[]
  country?: string
  regulatorColor?: string
  flag?: string
  desc?: string
  banner?: string
}

export type RegulatorDisclosureTrader = {
  [key: string]: unknown
  alias?: string
  Alias?: string
  code?: string
  Code?: string
  ico?: string
  Ico?: string
  icon?: string
  Icon?: string
  logo?: string
  Logo?: string
  showName?: string
  ShowName?: string
  localShortName?: string
  LocalShortName?: string
  localFullName?: string
  LocalFullName?: string
  englishShortName?: string
  EnglishShortName?: string
  englishFullName?: string
  EnglishFullName?: string
  shortName?: string
  ShortName?: string
}

export type RegulatorDisclosureAiModuleSectionItem = {
  title?: string
  risk?: string
  riskColor?: string
  content?: string
}

export type RegulatorDisclosureAiModuleSection = {
  name?: string
  contents?: RegulatorDisclosureAiModuleSectionItem[]
}

export type RegulatorDisclosureAiModuleInfo = {
  title?: string
  titleIcon?: string
  titleIcon2?: string
  violationAction?: RegulatorDisclosureAiModuleSection
  penaltyMethod?: RegulatorDisclosureAiModuleSection
  penaltyAmount?: {
    name?: string
  }
}

export type RegulatorDisclosureDetailData = {
  title?: string
  content?: string
  originalContent?: string
  originalUrl?: string
  urlAddress?: string
  regTime?: string
  wiki_timestamp?: string | number
  imgs?: string[]
  shareUrl?: string
  ruleName?: string
  reason?: string
  penaltyAmount?: string | null
  aiTimeStamp?: string | number
  regInfo?: RegulatorDisclosureRegInfo
  tag?: RegulatorDisclosureTag
  traders?: RegulatorDisclosureTrader[]
  aiModuleInfo?: RegulatorDisclosureAiModuleInfo
  categoryName?: string
  categoryCode?: string
  categoryId?: string
  rule?: number
  regulatorCode?: string
  amountSymbol?: string
  seal?: string
  isOriginal?: boolean
  enclosure?: string | null
}

type RegulatorDisclosureDetailWrappedResponse = {
  data?: RegulatorDisclosureDetailData
  Data?: RegulatorDisclosureDetailData
  result?: RegulatorDisclosureDetailData
  succeed?: boolean
  code?: number
  message?: string
}

type RegulatorDisclosureDetailRawData = {
  Title?: string
  Content?: string
  OriginalContent?: string
  OriginalUrl?: string
  UrlAddress?: string
  RegTime?: string
  wiki_timestamp?: string | number
  Imgs?: string[]
  ShareUrl?: string
  RuleName?: string
  Reason?: string | null
  PenaltyAmount?: string | null
  AiTimeStamp?: string | number
  RegInfo?: {
    fullName?: string
    FullName?: string
    enFullname?: string
    EnFullname?: string
    shortName?: string
    ShortName?: string
    logo?: string
    Logo?: string
    bgColor?: string
    BgColor?: string
    time?: string
    Time?: string
    websites?: string[]
    Websites?: string[]
    labels?: string[]
    Labels?: string[]
    country?: string
    Country?: string
    regulatorColor?: string
    RegulatorColor?: string
    flag?: string
    Flag?: string
    desc?: string
    Desc?: string
    banner?: string
    Banner?: string
  }
  Tag?: {
    TagName?: string
    Color?: string
    Category?: string
    Language?: string
    Logo?: string
  }
  Traders?: RegulatorDisclosureTrader[]
  AiModuleInfo?: RegulatorDisclosureAiModuleInfo
  CategoryName?: string
  CategoryCode?: string
  CategoryId?: string
  Rule?: number
  RegulatorCode?: string
  AmountSymbol?: string
  Seal?: string
  IsOriginal?: boolean
  Enclosure?: string | null
}

type RegulatorDisclosureListResponse = {
  data?: unknown
  Data?: unknown
  result?: unknown
  list?: unknown
  items?: unknown
}

const unwrapResponseData = (value: unknown): RegulatorDisclosureDetailRawData & RegulatorDisclosureDetailData => {
  let source = value as Record<string, unknown>

  while (
    source &&
    typeof source === 'object' &&
    !Array.isArray(source) &&
    !('Title' in source) &&
    !('title' in source)
  ) {
    const next = source.result ?? source.data ?? source.Data
    if (!next || next === source || typeof next !== 'object' || Array.isArray(next)) {
      break
    }
    source = next as Record<string, unknown>
  }

  return source as RegulatorDisclosureDetailRawData & RegulatorDisclosureDetailData
}

const normalizeText = (value: unknown) => {
  if (Array.isArray(value)) {
    return normalizeText(value[0])
  }

  return value === null || value === undefined ? '' : String(value).trim()
}

const normalizeStringList = (value: unknown) => {
  if (!Array.isArray(value)) return []
  return value.map((item) => normalizeText(item)).filter(Boolean)
}

const normalizeTrader = (item: RegulatorDisclosureTrader | null | undefined): RegulatorDisclosureTrader => {
  return {
    ...item,
    alias: normalizeText(item?.alias || item?.Alias),
    code: normalizeText(item?.code || item?.Code),
    ico: normalizeText(item?.ico || item?.Ico),
    icon: normalizeText(item?.icon || item?.Icon || item?.ico || item?.Ico),
    logo: normalizeText(item?.logo || item?.Logo || item?.ico || item?.Ico),
    showName: normalizeText(item?.showName || item?.ShowName),
    localShortName: normalizeText(item?.localShortName || item?.LocalShortName),
    localFullName: normalizeText(item?.localFullName || item?.LocalFullName),
    englishShortName: normalizeText(item?.englishShortName || item?.EnglishShortName),
    englishFullName: normalizeText(item?.englishFullName || item?.EnglishFullName),
    shortName: normalizeText(item?.shortName || item?.ShortName),
  }
}

const normalizeDisclosureDetail = (
  response: RegulatorDisclosureDetailWrappedResponse | RegulatorDisclosureDetailData | RegulatorDisclosureDetailRawData,
): RegulatorDisclosureDetailData => {
  const source = unwrapResponseData(response)

  const regInfo = source.RegInfo || source.regInfo || {}
  const tag = source.Tag || source.tag || {}

  return {
    title: normalizeText(source.Title || source.title),
    content: normalizeText(source.Content || source.content),
    originalContent: normalizeText(source.OriginalContent || source.originalContent),
    originalUrl: normalizeText(
      source.UrlAddress ||
        source.urlAddress ||
        source.OriginalUrl ||
        source.originalUrl ||
        regInfo.websites?.[0] ||
        regInfo.Websites?.[0],
    ),
    urlAddress: normalizeText(source.UrlAddress || source.urlAddress),
    regTime: normalizeText(source.RegTime || source.regTime),
    wiki_timestamp: source.wiki_timestamp,
    imgs: normalizeStringList(source.Imgs || source.imgs),
    shareUrl: normalizeText(source.ShareUrl || source.shareUrl),
    ruleName: normalizeText(source.RuleName || source.ruleName),
    reason: normalizeText(source.Reason || source.reason),
    penaltyAmount: source.PenaltyAmount ?? source.penaltyAmount ?? null,
    aiTimeStamp: source.AiTimeStamp || source.aiTimeStamp,
    regInfo: {
      fullName: normalizeText(regInfo.fullName || regInfo.FullName),
      enFullname: normalizeText(regInfo.enFullname || regInfo.EnFullname),
      shortName: normalizeText(regInfo.shortName || regInfo.ShortName),
      logo: normalizeText(regInfo.logo || regInfo.Logo),
      themeColor: normalizeText(regInfo.themeColor || regInfo.bgColor || regInfo.BgColor),
      time: normalizeText(regInfo.time || regInfo.Time || regInfo.labels?.[0] || regInfo.Labels?.[0]),
      websites: normalizeStringList(regInfo.websites || regInfo.Websites),
      labels: normalizeStringList(regInfo.labels || regInfo.Labels),
      country: normalizeText(regInfo.country || regInfo.Country),
      regulatorColor: normalizeText(regInfo.regulatorColor || regInfo.RegulatorColor),
      flag: normalizeText(regInfo.flag || regInfo.Flag),
      desc: normalizeText(regInfo.desc || regInfo.Desc),
      banner: normalizeText(regInfo.banner || regInfo.Banner),
    },
    tag: {
      tagName: normalizeText(tag.TagName || tag.tagName),
      color: normalizeText(tag.Color || tag.color),
      category: normalizeText(tag.Category || tag.category),
      language: normalizeText(tag.Language || tag.language),
      logo: normalizeText(tag.Logo || tag.logo),
    },
    traders: Array.isArray(source.Traders || source.traders)
      ? (source.Traders || source.traders || []).map((item) => normalizeTrader(item))
      : [],
    aiModuleInfo: source.AiModuleInfo || source.aiModuleInfo,
    categoryName: normalizeText(source.CategoryName || source.categoryName),
    categoryCode: normalizeText(source.CategoryCode || source.categoryCode),
    categoryId: normalizeText(source.CategoryId || source.categoryId),
    rule: source.Rule ?? source.rule,
    regulatorCode: normalizeText(source.RegulatorCode || source.regulatorCode),
    amountSymbol: normalizeText(source.AmountSymbol || source.amountSymbol),
    seal: normalizeText(source.Seal || source.seal),
    isOriginal: Boolean(source.IsOriginal ?? source.isOriginal),
    enclosure: (source.Enclosure ?? source.enclosure ?? null) as string | null,
  }
}

export const getRegulatorDisclosureDetail = async (slug: string): Promise<RegulatorDisclosureDetailData | null> => {
  if (!slug) {
    return null
  }

  const api = useEnvApi()
  const requestConfig = {
    baseURL: api.wikibitBffGo,
    url: REGULATOR_DISCLOSURE_DETAIL_API_PATH,
    method: 'GET' as const,
    params: { slug },
  }

  const response = import.meta.server
    ? await useServerFetch<RegulatorDisclosureDetailWrappedResponse | RegulatorDisclosureDetailData>(requestConfig)
    : await request<unknown, RegulatorDisclosureDetailWrappedResponse | RegulatorDisclosureDetailData>(requestConfig)

  if (!response) {
    return null
  }

  return normalizeDisclosureDetail(response)
}

/** 按监管机构获取披露列表，用于详情接口未返回关联披露时的兜底数据源。 */
export const getRegulatorDisclosureList = async (regulatorCode: string): Promise<Record<string, unknown>[]> => {
  if (!regulatorCode) {
    return []
  }

  const api = useEnvApi()
  const requestConfig = {
    baseURL: api.v4,
    url: REGULATOR_DISCLOSURE_LIST_API_PATH,
    method: 'GET' as const,
    params: {
      cate: 'all',
      regulatorCodes: regulatorCode,
      pageindex: 1,
      pagesize: 4,
    },
  }

  const response = import.meta.server
    ? await useServerFetch<RegulatorDisclosureListResponse>(requestConfig)
    : await request<unknown, RegulatorDisclosureListResponse>(requestConfig)

  let source: unknown = response
  for (let index = 0; index < 3 && source && typeof source === 'object' && !Array.isArray(source); index += 1) {
    const record = source as Record<string, unknown>
    const next =
      record.list ?? record.items ?? record.rows ?? record.records ?? record.data ?? record.Data ?? record.result
    if (Array.isArray(next)) {
      source = next
      break
    }
    if (!next || next === source) break
    source = next
  }

  const items = Array.isArray(source)
    ? source.filter((item): item is Record<string, unknown> => Boolean(item && typeof item === 'object'))
    : []


  return items
}
